import React from 'react';
import { Box, keyframes, Tooltip } from '@mui/material';

interface NavBadgeProps {
  /** Numeric count to display. When 0/undefined, the badge is hidden. */
  count?: number;
  /** If true, renders as a tiny pulsing dot (sidebar collapsed). */
  compact?: boolean;
  /** Absolute-position the dot on top-right of the parent icon (compact only). */
  floating?: boolean;
  /** Optional aria/tooltip label describing what the count represents. */
  label?: string;
}

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.55);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
`;

/**
 * Modern SaaS-style notification indicator for sidebar navigation items.
 *
 * - Expanded sidebar: small red pill with the number (capped at "99+")
 * - Collapsed sidebar: tiny pulsing dot overlaid on the icon
 */
const NavBadge: React.FC<NavBadgeProps> = ({ count, compact = false, floating = false, label }) => {
  if (!count || count <= 0) return null;
  const display = count > 99 ? '99+' : String(count);

  if (compact) {
    const dot = (
      <Box
        aria-label={label}
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: '#ef4444',
          animation: `${pulse} 1.8s ease-out infinite`,
          ...(floating
            ? {
              position: 'absolute',
              top: 6,
              right: 6,
              border: (theme) => `2px solid ${theme.palette.background.sidebar}`,
              boxSizing: 'content-box',
            }
            : {}),
        }}
      />
    );
    return label ? (
      <Tooltip title={label} placement="right" arrow>
        {dot}
      </Tooltip>
    ) : (
      dot
    );
  }

  return (
    <Box
      aria-label={label}
      sx={{
        ml: 'auto',
        minWidth: 18,
        height: 18,
        px: 0.6,
        borderRadius: 999,
        bgcolor: '#ef4444',
        color: '#fff',
        fontSize: 10.5,
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: 0.2,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.18)',
        animation: `${pulse} 2.4s ease-out infinite`,
      }}
    >
      {display}
    </Box>
  );
};

export default NavBadge;
